"use client";

import {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useEffect
} from "react";

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
}) {
  const styles: Record<string, string> = {
    primary:
      "bg-brand-600 text-pure-white shadow-sm hover:bg-brand-700 active:bg-brand-800 focus-visible:ring-brand-500",
    secondary:
      "bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 active:bg-gray-100 focus-visible:ring-brand-500",
    danger:
      "bg-danger-500 text-pure-white shadow-sm hover:bg-danger-600 active:bg-danger-700 focus-visible:ring-danger-500",
    ghost:
      "bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 focus-visible:ring-gray-400"
  };
  const sizes: Record<string, string> = {
    sm: "px-2.5 py-1.5 text-xs gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-5 py-2.5 text-sm gap-2"
  };
  return (
    <button
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant]} ${sizes[size]} ${className}`}
      {...props}
    />
  );
}

const fieldBase =
  "w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500";
const fieldOk = "border-gray-300 focus:border-brand-500 focus:ring-brand-500/25";
const fieldErr = "border-danger-400 focus:border-danger-500 focus:ring-danger-500/25";
const fieldClass = `${fieldBase} ${fieldOk}`;

export function Input({
  label,
  hint,
  error,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string; error?: string }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>}
      <input className={`${fieldBase} ${error ? fieldErr : fieldOk} ${className}`} aria-invalid={error ? true : undefined} {...props} />
      {error ? (
        <span className="mt-1 block text-xs text-danger-600">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>
      )}
    </label>
  );
}

export function Textarea({
  label,
  hint,
  error,
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string; error?: string }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>}
      <textarea className={`${fieldBase} ${error ? fieldErr : fieldOk} ${className}`} aria-invalid={error ? true : undefined} {...props} />
      {error ? (
        <span className="mt-1 block text-xs text-danger-600">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>
      )}
    </label>
  );
}

export function Select({
  label,
  children,
  className = "",
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>}
      <select
        className={`${fieldClass} cursor-pointer appearance-none bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-9 ${className}`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19.5 8.25l-7.5 7.5-7.5-7.5'/%3E%3C/svg%3E\")"
        }}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Card({
  title,
  actions,
  children,
  className = "",
  // Tighter on phones so the content isn't squeezed by padding on a 360px screen.
  bodyClassName = "p-4 sm:p-5",
  hover = false
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  hover?: boolean;
}) {
  return (
    <section
      className={`rounded-2xl border border-gray-200/70 bg-white shadow-card ${
        hover ? "transition duration-200 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover" : ""
      } ${className}`}
    >
      {(title || actions) && (
        // flex-wrap: on a narrow screen a wide action (search box + button) must drop below the
        // title instead of pushing the header out of the card.
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-5">
          {title && <h2 className="text-sm font-semibold tracking-tight text-gray-900">{title}</h2>}
          {actions}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

type Tone = "gray" | "green" | "red" | "blue" | "amber";

export function Badge({
  children,
  tone = "gray",
  dot = false
}: {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}) {
  const tones: Record<Tone, string> = {
    gray: "bg-gray-100 text-gray-600 ring-gray-200",
    green: "bg-success-50 text-success-700 ring-success-200 dark:bg-success-500/15 dark:text-success-300 dark:ring-success-500/30",
    red: "bg-danger-50 text-danger-700 ring-danger-200 dark:bg-danger-500/15 dark:text-danger-300 dark:ring-danger-500/30",
    blue: "bg-accent-50 text-accent-700 ring-accent-200 dark:bg-accent-500/15 dark:text-accent-300 dark:ring-accent-500/30",
    amber: "bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-500/15 dark:text-warning-300 dark:ring-warning-500/30"
  };
  const dotColor: Record<Tone, string> = {
    gray: "bg-gray-400",
    green: "bg-success-500",
    red: "bg-danger-500",
    blue: "bg-accent-500",
    amber: "bg-warning-500"
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}
    >
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${dotColor[tone]}`} />}
      {children}
    </span>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-gray-200/70 ${className}`} />;
}

export function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-gray-100 pb-4">
      <div className="flex min-w-0 items-stretch gap-3">
        <span className="w-1 shrink-0 rounded-full bg-brand-500" aria-hidden />
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatusPill({
  ok,
  label,
  pulse
}: {
  ok: boolean | null;
  label: string;
  pulse?: boolean;
}) {
  const color = ok === null ? "bg-gray-300" : ok ? "bg-success-500" : "bg-danger-500";
  const text = ok === null ? "text-gray-500" : ok ? "text-success-700 dark:text-success-300" : "text-danger-700 dark:text-danger-300";
  const ring = ok === null ? "ring-gray-200" : ok ? "ring-success-200 dark:ring-success-500/30" : "ring-danger-200 dark:ring-danger-500/30";
  const bg = ok === null ? "bg-gray-50" : ok ? "bg-success-50 dark:bg-success-500/15" : "bg-danger-50 dark:bg-danger-500/15";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${bg} ${text} ${ring}`}>
      <span className="relative flex h-2 w-2">
        {pulse && ok && (
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${color} opacity-75`} />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${color}`} />
      </span>
      {label}
    </span>
  );
}

export function EmptyState({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 px-6 py-12 text-center">
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  widthClass = "max-w-md"
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  widthClass?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 animate-fade-in bg-pure-black/50 backdrop-blur-sm" onClick={onClose} />
      {/* max-h + a scrollable BODY (header and footer stay pinned): without this a tall dialog on a
          short viewport — a landscape phone, a small laptop — ran off the screen with its buttons
          unreachable. flex-col keeps the footer visible however tall the content gets. */}
      <div className={`relative flex max-h-[92vh] w-full flex-col ${widthClass} animate-scale-in rounded-2xl bg-white shadow-dropdown ring-1 ring-black/5`}>
        {title && (
          <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3.5 sm:px-5">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <button onClick={onClose} className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-100 px-4 py-3.5 sm:gap-3 sm:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Alert({ kind = "error", children }: { kind?: "error" | "success"; children: ReactNode }) {
  if (!children) return null;
  const styles =
    kind === "error"
      ? "border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/15 dark:text-danger-300"
      : "border-success-200 bg-success-50 text-success-700 dark:border-success-500/30 dark:bg-success-500/15 dark:text-success-300";
  const icon =
    kind === "error"
      ? "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      : "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z";
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${styles}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="mt-0.5 h-4 w-4 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
      </svg>
      <span>{children}</span>
    </div>
  );
}
